// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// `pose-v1` — where another player's head and hands are, fifteen times a second (M8-03).
//
// **A pose is disposable and that changes every decision in this file.** 10 §5 puts `pose-v1` on
// a lane that is never durable: a packet that arrives late is worthless, a packet that is lost is
// not worth asking for again, and nothing here may block, retry or persist. What matters is that
// the *next* one arrives, and that what is drawn between packets is a plausible continuation of
// what came before rather than a jump.
//
// **The frame is playfield-local** (06 §4, and see `venueFrame.ts` for why 10 §5's "venue-local"
// was the half that changed). A pose says where a player is relative to *the machine they are at*.
// The receiver composes it with their own idea of where that bay stands, which is what lets two
// players in two different real rooms — one of them with no room at all, in passthrough — line up
// correctly against the same cabinet.
//
// **Orientation is a yaw, a pitch and a roll rather than a quaternion**, and that is not a
// shortcut. The frame conversion is a rotation about Y: yaw composes with the machine's yaw, and
// pitch and roll pass through untouched, exactly as height does for position. A quaternion would
// need its own conversion, its own fixture and its own opportunity to disagree between two
// clients, in exchange for expressing nothing this needs.
//
// No clock in this module. Every function takes the time it should act at, so the whole of the
// cadence, the interpolation and the fade can be decided in a test rather than waited out.

import { toPlayfield, toVenue, yawToPlayfield, yawToVenue, type VenuePlacement } from './venueFrame'

/**
 * The lane 10 §5 names for poses. An event name on the venue channel, not a channel of its own.
 *
 * Here rather than in the client that subscribes to it, which is where it lived until G5-06. Every
 * other lane in the product names itself from the module that owns its wire — `TRACE_EVENT` beside
 * `writeDelta`, `VOICE_OFFER_EVENT` beside `encodeDescription` — and this one did not, so the
 * generated `asyncapi.yaml` would have had to read a name out of a client to describe a payload
 * defined here.
 */
export const POSE_EVENT = 'pose-v1'

/** How often a client sends, from 10 §5. */
export const POSE_HZ = 15

/** …which is this many milliseconds apart. */
export const POSE_INTERVAL_MS = 1000 / POSE_HZ

/**
 * How far behind live the receiver draws, milliseconds.
 *
 * **This is what makes it interpolation rather than extrapolation.** Drawing the newest sample the
 * instant it arrives means that between packets there is nothing ahead to aim at, so a renderer
 * either freezes for 66 ms at a time or guesses — and a guessed head that is corrected 66 ms later
 * is a head that jitters. Holding the picture one and a half packet intervals behind means there is
 * almost always a sample on each side of the moment being drawn, so **one lost packet still
 * interpolates** instead of showing.
 *
 * 100 ms is the cost: another player's head is a tenth of a second behind where they actually are.
 * Nobody can see that on somebody else's avatar, and everybody can see a stutter.
 */
export const RENDER_DELAY_MS = 100

/** Silence this long and the avatar begins to fade (06 §4). */
export const FADE_AFTER_MS = 250

/** Silence this long and it is gone (06 §4). */
export const HIDE_AFTER_MS = 1000

/** A head or a wrist, in the playfield-local frame of the machine at that bay. */
export interface AvatarPose {
  readonly x: number
  readonly y: number
  readonly z: number
  /** Radians, `Placement`'s convention — the one `venueFrame.yawToVenue` composes. */
  readonly yaw: number
  /** Radians. Passes through the frame conversion untouched, as height does. */
  readonly pitch: number
  readonly roll: number
}

/**
 * One client's whole posture at one instant.
 *
 * Wrists are optional because a player may have no hands to report: a controller can be put down,
 * hand tracking loses a hand behind the cabinet, and a web player on a phone has never had one.
 * `null` is the honest answer and it is not the same as a wrist at the origin, which is a hand
 * inside the machine.
 */
export interface PosePacket {
  readonly participantId: string
  /** Which bay this pose is relative to. Without it, the pose is relative to nothing. */
  readonly bay: number
  readonly head: AvatarPose
  readonly leftWrist: AvatarPose | null
  readonly rightWrist: AvatarPose | null
  /**
   * The sender's own sequence number, counting packets rather than milliseconds.
   *
   * **Not a timestamp**, because two clients' clocks disagree by more than the thing being
   * measured and nothing here needs wall-clock time — only ordering. A packet that arrives out of
   * order is dropped by comparing this, and the receiver times everything by when *it* received
   * something, which is a clock it can trust.
   */
  readonly sequence: number
}

/**
 * A pose measured in the room, expressed against the machine — what a sender does before sending.
 *
 * **This is where the choice of yaw-pitch-roll pays for itself.** The frame conversion is a
 * rotation about Y, so position goes through `venueFrame`, yaw composes with the machine's, and
 * pitch and roll pass through untouched — a head tilted 20° is tilted 20° whichever way the
 * cabinet is turned. A quaternion would need a second conversion, a second fixture and a second
 * chance for two clients to disagree, and would express nothing more.
 */
export function poseInPlayfield(pose: AvatarPose, machine: VenuePlacement): AvatarPose {
  const at = toPlayfield({ x: pose.x, y: pose.y, z: pose.z }, machine)
  return { ...at, yaw: yawToPlayfield(pose.yaw, machine), pitch: pose.pitch, roll: pose.roll }
}

/**
 * And back: a received pose placed against the receiver's own idea of where that bay stands.
 *
 * The exact inverse, and the reason the wire carries a bay index rather than a position. In a
 * venue, `machine` is the bay's placement. **In passthrough there is no venue**, and `machine` is
 * wherever this player has stood their one cabinet — which is why a remote player lands at the
 * machine rather than three metres away in a room that does not exist.
 */
export function poseInVenue(pose: AvatarPose, machine: VenuePlacement): AvatarPose {
  const at = toVenue({ x: pose.x, y: pose.y, z: pose.z }, machine)
  return { ...at, yaw: yawToVenue(pose.yaw, machine), pitch: pose.pitch, roll: pose.roll }
}

/** Metres and radians, rounded to what a pose can actually express. */
const MM = 1000
const MILLIRAD = 1000

function round(value: number, scale: number): number {
  return Math.round(value * scale) / scale
}

function encodePose(pose: AvatarPose): readonly number[] {
  // A fixed-length array rather than an object, because this is the one payload that goes out
  // fifteen times a second per player and the field names would be most of it. Rounded to a
  // millimetre and a milliradian: finer than that is describing the noise in a tracking system,
  // and it costs bytes on every packet for ever.
  return [
    round(pose.x, MM),
    round(pose.y, MM),
    round(pose.z, MM),
    round(pose.yaw, MILLIRAD),
    round(pose.pitch, MILLIRAD),
    round(pose.roll, MILLIRAD),
  ]
}

function decodePose(raw: unknown): AvatarPose | undefined {
  if (!Array.isArray(raw) || raw.length < 6) return undefined
  for (const value of raw) {
    // A NaN in a pose is a head that vanishes from the scene graph in most renderers, and it
    // propagates: interpolating towards it poisons every frame after. Refused at the door.
    if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  }
  const [x, y, z, yaw, pitch, roll] = raw as number[]
  return { x: x!, y: y!, z: z!, yaw: yaw!, pitch: pitch!, roll: roll! }
}

export function encodePosePacket(packet: PosePacket): Record<string, unknown> {
  return {
    participant_id: packet.participantId,
    bay: packet.bay,
    seq: packet.sequence,
    head: encodePose(packet.head),
    left: packet.leftWrist === null ? null : encodePose(packet.leftWrist),
    right: packet.rightWrist === null ? null : encodePose(packet.rightWrist),
  }
}

/**
 * Read a pose packet, or `undefined` if it is not one.
 *
 * Unknown fields are ignored — the same additive-within-major policy the rest of the contract is
 * held to (03 §5), and it matters here because these rows are written by other clients which may
 * be a version ahead. A malformed *wrist* costs the wrist and not the packet: a head with one hand
 * is a thing that happens, and throwing the head away for it would be worse.
 */
export function decodePosePacket(raw: unknown): PosePacket | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const row = raw as Record<string, unknown>
  const participantId = row['participant_id']
  const bay = row['bay']
  const sequence = row['seq']
  if (typeof participantId !== 'string' || participantId === '') return undefined
  if (typeof bay !== 'number' || !Number.isInteger(bay) || bay < 0) return undefined
  if (typeof sequence !== 'number' || !Number.isFinite(sequence)) return undefined
  const head = decodePose(row['head'])
  if (head === undefined) return undefined
  return {
    participantId,
    bay,
    sequence,
    head,
    leftWrist: decodePose(row['left']) ?? null,
    rightWrist: decodePose(row['right']) ?? null,
  }
}

/**
 * Whether it is time to send again.
 *
 * A gate rather than a timer, because the caller already has a frame loop and a second scheduler
 * running beside it is a second thing to pause, recycle and get wrong. At 72 Hz this answers yes
 * about every fifth frame.
 *
 * `lastSentMs` of `undefined` means nothing has been sent, and the answer is yes: the first pose
 * should leave immediately rather than up to 66 ms later, because it is the one that makes the
 * player appear at all.
 */
export function shouldSend(nowMs: number, lastSentMs: number | undefined): boolean {
  if (lastSentMs === undefined) return true
  // `>=` and not `>`: a frame that lands exactly on the interval is on time, not early. And a
  // clock that has gone backwards — which happens across a session recycle — sends rather than
  // waiting out a negative interval that would never elapse.
  return nowMs - lastSentMs >= POSE_INTERVAL_MS || nowMs < lastSentMs
}

/** What a receiver should draw for one player right now. */
export interface DrawnPose {
  readonly bay: number
  readonly head: AvatarPose
  readonly leftWrist: AvatarPose | null
  readonly rightWrist: AvatarPose | null
  /** 1 while fresh, falling to 0 across the fade, and never drawn at 0. */
  readonly opacity: number
}

/**
 * How solid an avatar should be, given how long ago its last packet arrived.
 *
 * Fully solid until 250 ms, then a linear fade to nothing at 1 s (06 §4). **The fade is the
 * feature.** A player whose connection stutters for a third of a second should not blink out and
 * back; a player who has actually gone should not be left standing there looking present. A fade
 * says "we are losing them" with no words and no wrong answer, and it is reversible the instant a
 * packet arrives.
 */
export function stalenessOpacity(sinceMs: number): number {
  if (sinceMs <= FADE_AFTER_MS) return 1
  if (sinceMs >= HIDE_AFTER_MS) return 0
  return (HIDE_AFTER_MS - sinceMs) / (HIDE_AFTER_MS - FADE_AFTER_MS)
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** Shortest-way-round angle interpolation. */
function lerpAngle(a: number, b: number, t: number): number {
  let delta = b - a
  while (delta > Math.PI) delta -= 2 * Math.PI
  while (delta < -Math.PI) delta += 2 * Math.PI
  return a + delta * t
}

/**
 * Blend two poses. Exported because it is the whole of "with interpolation" and the fixtures run
 * it directly — a receiver's smoothness is a contract between clients, not an implementation
 * detail of one.
 */
export function interpolate(a: AvatarPose, b: AvatarPose, t: number): AvatarPose {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    z: lerp(a.z, b.z, t),
    // **Angles the short way round, or a head spins the wrong way through 350°.** A player facing
    // just west of north crosses the ±π seam constantly, and a linear blend from 3.1 to −3.1 is a
    // full rotation in the wrong direction, every time.
    yaw: lerpAngle(a.yaw, b.yaw, t),
    pitch: lerpAngle(a.pitch, b.pitch, t),
    roll: lerpAngle(a.roll, b.roll, t),
  }
}

/** A wrist that is present in one sample and absent in the other does not interpolate. */
function lerpWrist(a: AvatarPose | null, b: AvatarPose | null, t: number): AvatarPose | null {
  if (a === null || b === null) {
    // Take the newer answer rather than blending towards nothing: a hand that has just been put
    // down should disappear, not shrink towards the origin, which is inside the machine.
    return t >= 0.5 ? b : a
  }
  return interpolate(a, b, t)
}

interface Sample {
  readonly at: number
  readonly packet: PosePacket
}

/**
 * One remote player's poses, and what to draw from them.
 *
 * Two samples are kept, not a long history: the buffer's whole job is to have something on each
 * side of the moment being drawn, and a third sample is a third opportunity to draw a pose from
 * two seconds ago after a stall.
 */
export class PoseTrack {
  private previous: Sample | undefined
  private latest: Sample | undefined

  /**
   * Take a packet that arrived at `atMs`.
   *
   * Returns false for one that is out of order, which a lossy lane produces routinely. Accepting
   * it would move the avatar backwards and then forwards again — a visible twitch, from a packet
   * whose information was already superseded.
   */
  receive(packet: PosePacket, atMs: number): boolean {
    if (this.latest !== undefined && packet.sequence <= this.latest.packet.sequence) return false
    this.previous = this.latest
    this.latest = { at: atMs, packet }
    return true
  }

  /** When the newest packet arrived, or `undefined` if none ever has. */
  get lastArrivalMs(): number | undefined {
    return this.latest?.at
  }

  /**
   * What to draw at `nowMs`, or `undefined` for an avatar that should not be drawn at all.
   *
   * The moment drawn is `nowMs - RENDER_DELAY_MS`, which normally falls between the two samples
   * held. Before the first two packets, or after a stall long enough that the delayed moment is
   * past the newest sample, the newest sample is held still rather than extrapolated — a frozen
   * head that is about to fade reads as a connection problem, which it is, and an extrapolated one
   * reads as a player drifting through the cabinet.
   */
  draw(nowMs: number): DrawnPose | undefined {
    if (this.latest === undefined) return undefined
    const opacity = stalenessOpacity(nowMs - this.latest.at)
    if (opacity <= 0) return undefined

    const at = nowMs - RENDER_DELAY_MS
    const from = this.previous
    const to = this.latest
    if (from === undefined || at >= to.at) {
      return {
        bay: to.packet.bay,
        head: to.packet.head,
        leftWrist: to.packet.leftWrist,
        rightWrist: to.packet.rightWrist,
        opacity,
      }
    }
    if (at <= from.at) {
      return {
        bay: from.packet.bay,
        head: from.packet.head,
        leftWrist: from.packet.leftWrist,
        rightWrist: from.packet.rightWrist,
        opacity,
      }
    }

    const span = to.at - from.at
    const t = span <= 0 ? 1 : (at - from.at) / span
    return {
      // **The bay does not interpolate.** A player who teleports between bays is at one or the
      // other, and half way is inside the machine between them.
      bay: to.packet.bay,
      head: interpolate(from.packet.head, to.packet.head, t),
      leftWrist: lerpWrist(from.packet.leftWrist, to.packet.leftWrist, t),
      rightWrist: lerpWrist(from.packet.rightWrist, to.packet.rightWrist, t),
      opacity,
    }
  }
}

/**
 * Every remote player's poses.
 *
 * Keyed by participant, and **pruned by the roster rather than by silence**: a player who has left
 * is removed because presence said so, and a player who has gone quiet fades but stays, because
 * they are still in the room and their next packet should bring them straight back.
 */
export class PoseTracks {
  private tracks = new Map<string, PoseTrack>()

  receive(packet: PosePacket, atMs: number): boolean {
    let track = this.tracks.get(packet.participantId)
    if (track === undefined) {
      track = new PoseTrack()
      this.tracks.set(packet.participantId, track)
    }
    return track.receive(packet, atMs)
  }

  /** What to draw for everyone worth drawing, at `nowMs`. */
  draw(nowMs: number): ReadonlyMap<string, DrawnPose> {
    const drawn = new Map<string, DrawnPose>()
    for (const [id, track] of this.tracks) {
      const pose = track.draw(nowMs)
      if (pose !== undefined) drawn.set(id, pose)
    }
    return drawn
  }

  /** Forget everyone presence no longer lists. */
  keepOnly(participantIds: Iterable<string>): void {
    const keep = new Set(participantIds)
    for (const id of [...this.tracks.keys()]) {
      if (!keep.has(id)) this.tracks.delete(id)
    }
  }

  get size(): number {
    return this.tracks.size
  }
}
